import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { FilterSelect } from "@/components/common/FilterSelect";
import { ChannelBadge as RncChannelBadge } from "@/pages/signals/rnc/RncShared";
import { cn } from "@/lib/utils";
import { signalsApi } from "@/lib/api";
import {
  ACTIVE_SIGNAL_SOURCES,
  COMING_SOON_MESSAGE,
  DEFAULT_SEQUENCE_DESIGN_CHANNELS,
  isActiveSignalSource,
} from "@/lib/campaignSignals";

const PERSONAS = ["VP Sales", "CEO", "CTO", "Founder", "Ops Lead"];
const INDUSTRIES = ["SaaS", "Real Estate", "Fintech", "Healthcare", "Construction"];
const GOALS = ["Book demo", "Qualify lead", "Start conversation", "Re-engage"];
const TONES = ["Direct", "Consultative", "Warm", "Professional"];

/** @deprecated Prefer DEFAULT_SEQUENCE_DESIGN_CHANNELS — kept for callers that imported this Set. */
export const ASK_AGENT_SENDABLE_CHANNELS = new Set(DEFAULT_SEQUENCE_DESIGN_CHANNELS);

/**
 * A8 — Ask {agent} to design a sequence modal (production editor / create wizard).
 * Signal selection matches Campaign Creation Step 2 (catalog + V1 gating).
 */
export function AskAgentSequenceModal({
  open,
  agentName,
  generating,
  onOpenChange,
  onGenerate,
  initialSignalSources = null,
}) {
  const [persona, setPersona] = useState("VP Sales");
  const [industry, setIndustry] = useState("SaaS");
  const [goal, setGoal] = useState("Book demo");
  const [tone, setTone] = useState("Direct");
  const [signalSources, setSignalSources] = useState(["rnc"]);
  const [signalOptions, setSignalOptions] = useState([]);
  const [signalsLoading, setSignalsLoading] = useState(false);
  const [length, setLength] = useState(5);

  const title = useMemo(
    () => `Ask ${agentName} to design a sequence`,
    [agentName],
  );

  const dialogRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const seeded = Array.isArray(initialSignalSources)
      ? initialSignalSources.filter((k) => isActiveSignalSource(k))
      : [];
    setSignalSources(seeded.length ? seeded : ["rnc"]);
    return undefined;
  }, [open, initialSignalSources]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      setSignalsLoading(true);
      try {
        const data = await signalsApi.listCatalog();
        if (cancelled) return;
        const sources = Array.isArray(data?.sources) ? data.sources : [];
        setSignalOptions(sources);
      } catch {
        if (!cancelled) setSignalOptions([]);
      } finally {
        if (!cancelled) setSignalsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape" && !generating) onOpenChange(false);
    };
    const onClick = (e) => {
      if (e.target === dialogRef.current && !generating) onOpenChange(false);
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("click", onClick);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("click", onClick);
    };
  }, [open, generating, onOpenChange]);

  if (!open) return null;

  const toggleSignal = (key) => {
    if (!isActiveSignalSource(key)) {
      toast.message(COMING_SOON_MESSAGE);
      return;
    }
    setSignalSources((prev) => (
      prev.includes(key) ? prev.filter((c) => c !== key) : [...prev, key]
    ));
  };

  const selectedActive = signalSources.some((k) => ACTIVE_SIGNAL_SOURCES.has(k));

  const handleGenerate = () => {
    const allowed = signalSources.filter((k) => isActiveSignalSource(k));
    if (!allowed.length) {
      toast.error("Select at least one available Signal source (RNC or ABM List)");
      return;
    }
    onGenerate({
      persona,
      industry,
      goal,
      tone,
      signalSources: allowed,
      // Sequence designer still builds transport steps (email / WhatsApp / Telegram).
      channels: DEFAULT_SEQUENCE_DESIGN_CHANNELS,
      length,
    });
  };

  return (
    <dialog
      ref={dialogRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-transparent p-3 m-0 max-w-none max-h-none h-full w-full border-0"
      aria-modal="true"
      aria-labelledby="ask-agent-sequence-title"
      data-testid="ask-agent-sequence-modal"
      open
    >
      <div className="w-full max-w-lg max-h-[min(92dvh,720px)] overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-4 sm:px-5 py-4">
          <div className="min-w-0">
            <h2
              id="ask-agent-sequence-title"
              className="text-[15px] font-semibold text-slate-900 flex items-center gap-1.5"
            >
              <Sparkles size={15} className="text-violet-600 shrink-0" />
              {title}
            </h2>
            <p className="mt-1 text-[12.5px] text-slate-500">
              Tell {agentName} about your ideal customer and goal — {agentName} will draft
              the sequence and drop it on the canvas.
            </p>
          </div>
          <button
            type="button"
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 shrink-0"
            onClick={() => !generating && onOpenChange(false)}
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>

        <div className="space-y-4 px-4 sm:px-5 py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ask-persona" className="text-[11px]">Target Persona</Label>
              <FilterSelect
                id="ask-persona"
                variant="field"
                className="mt-1"
                value={persona}
                onChange={setPersona}
                options={PERSONAS.map((p) => ({ value: p, label: p }))}
                aria-label="Target Persona"
              />
            </div>
            <div>
              <Label htmlFor="ask-industry" className="text-[11px]">Industry</Label>
              <FilterSelect
                id="ask-industry"
                variant="field"
                className="mt-1"
                value={industry}
                onChange={setIndustry}
                options={INDUSTRIES.map((p) => ({ value: p, label: p }))}
                aria-label="Industry"
              />
            </div>
            <div>
              <Label htmlFor="ask-goal" className="text-[11px]">Goal</Label>
              <FilterSelect
                id="ask-goal"
                variant="field"
                className="mt-1"
                value={goal}
                onChange={setGoal}
                options={GOALS.map((p) => ({ value: p, label: p }))}
                aria-label="Goal"
              />
            </div>
            <div>
              <Label htmlFor="ask-tone" className="text-[11px]">Tone</Label>
              <FilterSelect
                id="ask-tone"
                variant="field"
                className="mt-1"
                value={tone}
                onChange={setTone}
                options={TONES.map((p) => ({ value: p, label: p }))}
                aria-label="Tone"
              />
            </div>
          </div>

          <div>
            <div className="text-[11px] font-medium text-slate-600 mb-1.5">Signals</div>
            <p className="text-[11px] text-slate-500 mb-2">
              Choose which Signal sources this sequence should target. Same options as Campaign Creation.
            </p>
            {signalsLoading && (
              <p className="text-[12px] text-slate-500" data-testid="ask-signals-loading">
                Loading Signal sources…
              </p>
            )}
            {!signalsLoading && (
              <fieldset className="flex flex-wrap gap-1.5" aria-label="Signals">
                {signalOptions.map((src) => {
                  const key = src.key;
                  const on = signalSources.includes(key);
                  return (
                    <button
                      type="button"
                      key={key}
                      title={src.name || key}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] border transition-colors",
                        on
                          ? "bg-violet-100 border-violet-400 text-violet-800"
                          : "bg-white border-slate-200 text-slate-600 hover:border-slate-300",
                      )}
                      onClick={() => toggleSignal(key)}
                      data-testid={`ask-signal-${key}`}
                    >
                      <RncChannelBadge channelKey={key} size={16} />
                      {src.name || key}
                    </button>
                  );
                })}
              </fieldset>
            )}
          </div>

          <div>
            <Label htmlFor="ask-length" className="text-[11px]">
              Length (steps) — {length}
            </Label>
            <input
              id="ask-length"
              type="range"
              min={3}
              max={9}
              value={length}
              onChange={(e) => setLength(Number(e.target.value))}
              className="mt-2 w-full accent-violet-600"
            />
          </div>
        </div>

        <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 border-t border-slate-100 px-4 sm:px-5 py-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-10 sm:h-8 w-full sm:w-auto"
            disabled={generating}
            onClick={() => onOpenChange(false)}
          >
            Discard
          </Button>
          <Button
            type="button"
            size="sm"
            className="h-10 sm:h-8 w-full sm:w-auto bg-violet-600 hover:bg-violet-700 text-white"
            disabled={generating || !selectedActive || signalsLoading}
            onClick={handleGenerate}
            data-testid="ask-agent-generate"
          >
            <Sparkles size={12} />
            {generating ? "Generating…" : "Generate Sequence"}
          </Button>
        </div>
      </div>
    </dialog>
  );
}

AskAgentSequenceModal.propTypes = {
  open: PropTypes.bool.isRequired,
  agentName: PropTypes.string.isRequired,
  generating: PropTypes.bool,
  onOpenChange: PropTypes.func.isRequired,
  onGenerate: PropTypes.func.isRequired,
  initialSignalSources: PropTypes.arrayOf(PropTypes.string),
};

AskAgentSequenceModal.defaultProps = {
  generating: false,
  initialSignalSources: null,
};
